import { useEffect } from "react";

const HAIRLINE = "var(--hairline)";

const rows = [
    {
        title: "Virtual try-on",
        detail: "One garment is 60 credits at 2K and 75 at 4K. A full look, 2 to 4 garments, is 120 at 2K.",
        credits: 60,
        unit: "per try-on",
    },
    {
        title: "Fashion model",
        detail: "A new model, a model swap, or a flat-lay. 60 credits. A face photo adds 45.",
        credits: 60,
        unit: "per image",
    },
    {
        title: "Generate and edit",
        detail: "Generate, reimagine, or edit. 27 credits at 2K. 4K is 48.",
        credits: 27,
        unit: "per image",
    },
];

function Price({ credits, unit }) {
    return (
        <div className="shrink-0 text-left sm:text-right">
            <div className="font-newsreader font-medium text-[40px] leading-none tracking-tight text-secondary tabular-nums">
                {credits}
            </div>
            <div className="mt-1 text-[13px] text-graphite">{unit}</div>
        </div>
    );
}

export default function Docs() {
    useEffect(() => {
        document.title = "Documentation | TryOn AI";
    }, []);

    return (
        <div className="h-screen overflow-y-scroll bg-ground font-Mulish text-graphite selection:bg-primary-100 selection:text-secondary">
            <div className="mx-auto max-w-[960px] px-6 pb-28 pt-12 sm:pb-32 sm:pt-16">
                <header className="mb-12 max-w-[42rem]">
                    <h1 className="font-newsreader font-medium text-4xl tracking-tight text-secondary sm:text-5xl">
                        API documentation
                    </h1>
                    <p className="mt-4 text-base leading-relaxed">
                        Integrate the API, or try each model in the playground.
                    </p>
                </header>

                <div
                    className="overflow-hidden rounded-2xl bg-white"
                    style={{ boxShadow: `0 0 0 1px ${HAIRLINE}` }}
                >
                    <section className="bg-primary-50 px-6 py-6 sm:px-8">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="max-w-[36rem]">
                                <h2 className="font-Mulish font-semibold text-lg text-secondary">Free credits</h2>
                                <p className="mt-1 text-sm leading-relaxed">
                                    Enough for one quality try-on and one generated image, with a little left over.
                                </p>
                            </div>
                            <Price credits={100} unit="when you sign up" />
                        </div>
                    </section>

                    {rows.map((row) => (
                        <section
                            key={row.title}
                            className="border-t px-6 py-5 transition-colors duration-150 hover:bg-hover-tint sm:px-8"
                            style={{ borderColor: HAIRLINE }}
                        >
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="max-w-[36rem]">
                                    <h2 className="font-Mulish font-semibold text-lg text-secondary">{row.title}</h2>
                                    <p className="mt-1 text-sm leading-relaxed">{row.detail}</p>
                                </div>
                                <Price credits={row.credits} unit={row.unit} />
                            </div>
                        </section>
                    ))}

                    <section
                        className="border-t px-6 py-6 sm:px-8"
                        style={{ borderColor: HAIRLINE }}
                    >
                        <h2 className="font-Mulish font-semibold text-lg text-secondary">Studio</h2>
                        <p className="mt-1 max-w-[36rem] text-sm leading-relaxed">
                            Background removal, upscale, caption, and chat. Crop and resize are free.
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {[
                                ["Background", "4"],
                                ["Upscale", "48"],
                                ["Caption", "1"],
                                ["Chat", "1"],
                                ["Crop", "Free"],
                            ].map(([label, credits]) => (
                                <span
                                    key={label}
                                    className="inline-flex items-baseline gap-1.5 rounded-full bg-ground px-3 py-1 text-sm text-graphite"
                                    style={{ boxShadow: `0 0 0 1px ${HAIRLINE}` }}
                                >
                                    {label}
                                    <span className="font-Mulish font-semibold text-base text-secondary tabular-nums">{credits}</span>
                                </span>
                            ))}
                        </div>
                    </section>

                    <section
                        className="border-t px-6 py-5 transition-colors duration-150 hover:bg-hover-tint sm:px-8"
                        style={{ borderColor: HAIRLINE }}
                    >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="max-w-[36rem]">
                                <h2 className="font-Mulish font-semibold text-lg text-secondary">Video</h2>
                                <p className="mt-1 text-sm leading-relaxed">
                                    640 credits for 8 seconds at 1080p.
                                </p>
                            </div>
                            <Price credits={640} unit="for 8 seconds" />
                        </div>
                    </section>

                    <section
                        className="border-t px-6 py-5 sm:px-8"
                        style={{ borderColor: HAIRLINE }}
                    >
                        <p className="text-sm leading-relaxed">
                            Need more credits?{" "}
                            <a href="mailto:contact@tryonlabs.ai" className="font-semibold text-primary-700 hover:underline">
                                Contact us
                            </a>
                        </p>
                    </section>
                </div>

                <div className="mt-10 flex justify-end">
                    <button
                        type="button"
                        onClick={() => window.open("https://docs.tryonlabs.ai", "_blank", "noopener,noreferrer")}
                        className="inline-flex w-full items-center justify-center rounded-full bg-primary-700 px-7 py-3.5 font-Mulish font-semibold text-base text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)] transition-colors duration-150 hover:bg-primary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:w-auto"
                    >
                        Visit documentation
                    </button>
                </div>
            </div>
        </div>
    );
}
